import { fmtQty } from './format';

/**
 * Variant box-packing helpers (frontend mirror of backend common/utils/packaging.ts).
 * `unitsPerBox` is a manually-configured per-variant COUNT — never derived from the variant's UoM/size.
 * Stock is always in base units; box/loose is entry + display only. Never divide by null/zero.
 */

const num = (v: any): number => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

/** Whether a variant/line actually has usable box packing. */
export function hasBoxPacking(p: { boxPackingEnabled?: boolean | null; unitsPerBox?: number | string | null }): boolean {
  return !!p.boxPackingEnabled && num(p.unitsPerBox) > 0;
}

/** Total base units for a box + loose entry. unitsPerBox falsy → boxes ignored, total = loose. */
export function calcTotalFromBoxLoose(boxQty: any, looseQty: any, unitsPerBox: any): number {
  const upb = num(unitsPerBox);
  return (upb > 0 ? num(boxQty) * upb : 0) + num(looseQty);
}

/** Split a total into whole boxes + loose remainder. unitsPerBox falsy → 0 boxes, all loose. */
export function splitBoxLoose(totalUnits: any, unitsPerBox: any): { boxes: number; loose: number } {
  const total = num(totalUnits);
  const upb = num(unitsPerBox);
  if (upb <= 0) return { boxes: 0, loose: total };
  const boxes = Math.floor(total / upb);
  return { boxes, loose: total - boxes * upb };
}

/**
 * "12 boxes + 7 loose" / "12 boxes" / "7 loose" from a total. Returns '' when there's no packing so callers
 * can fall back to a plain unit count.
 */
export function displayBoxLoose(totalUnits: any, unitsPerBox: any): string {
  const upb = num(unitsPerBox);
  if (upb <= 0) return '';
  const { boxes, loose } = splitBoxLoose(totalUnits, upb);
  const parts: string[] = [];
  if (boxes > 0) parts.push(`${fmtQty(boxes)} ${boxes === 1 ? 'box' : 'boxes'}`);
  if (loose > 0 || boxes === 0) parts.push(`${fmtQty(loose)} loose`);
  return parts.join(' + ');
}

/**
 * Box-count-led phrase for a stored line: "3 boxes × 10" / "3 boxes × 10 + 5 loose" / "" (no packing).
 * Reads the per-line snapshot (box/loose/unitsPerBox) so it shows exactly what was ordered.
 */
export function boxLoosePhrase(boxQty: any, looseQty: any, unitsPerBox: any): string {
  const upb = num(unitsPerBox);
  if (upb <= 0 || boxQty == null) return '';
  const b = num(boxQty), lo = num(looseQty);
  const parts: string[] = [];
  if (b > 0) parts.push(`${fmtQty(b)} ${b === 1 ? 'box' : 'boxes'} × ${fmtQty(upb)}`);
  if (lo > 0) parts.push(`${fmtQty(lo)} loose`);
  return parts.join(' + ');
}

/** "10 units/box" label, or 'Not configured' when packaging is off. */
export function boxPackingLabel(p: { boxPackingEnabled?: boolean | null; unitsPerBox?: number | string | null }): string {
  return hasBoxPacking(p) ? `${fmtQty(p.unitsPerBox)} units/box` : 'Not configured';
}

/** Validate a box/loose entry field. Returns an error string, or null when valid. */
export function validateBoxLoose(boxQty: any, looseQty: any): string | null {
  const box = num(boxQty), loose = num(looseQty);
  if (box < 0 || loose < 0) return 'Quantities cannot be negative';
  if (!Number.isInteger(box)) return 'Boxes must be a whole number';
  if (box === 0 && loose === 0) return 'Enter at least one box or loose unit';
  return null;
}
